import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { all_routes } from "../../../router/all_routes";
import {
  zones,
  districts,
  centerTypes,
} from "../../../../core/common/selectoption/selectoption";
import CommonSelect from "../../../../core/common/commonSelect";
import { useCenter } from "../hooks/useCenter";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { showToast } from "../../../../core/utils/alert";

// Define validation schema
const validationSchema = yup.object().shape({
  name: yup.string().required("Center name is required"),
  code: yup.string().required("Center code is required"),
  type: yup.string().required("Center type is required"),
  zone: yup.string().required("Zone is required"),
  phone: yup.string().matches(/^[0-9]{10}$/, "Phone number must contain only 10 numbers").required("Contact number is required"),
  email: yup.string().email("Invalid email").required("Email is required"),
  address: yup.string().required("Address is required"),
  district: yup.string().required("District is required"),
});

const AddCenter = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const {
    addNewCenter,
    updateCenter,
    isAdding,
    isUpdating,
    addError,
    useGetCenterById,
  } = useCenter();

  // State Management
  const [isEdit, setIsEdit] = useState<boolean>(false);
  const [formData, setFormData] = useState({
    name: "",
    code: "",
    type: "",
    zone: "",
    phone: "",
    email: "",
    address: "",
    district: "",
  });

  // Form setup with validation
  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm({
    resolver: yupResolver(validationSchema),
    defaultValues: formData,
  });

  // Document upload states
  const [photoFile, setPhotoFile] = useState<File | null>(null);
  const [idCardFile, setIdCardFile] = useState<File | null>(null);
  const [addressProofFile, setAddressProofFile] = useState<File | null>(null);
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const [idCardPreview, setIdCardPreview] = useState<string | null>(null);
  const [addressProofPreview, setAddressProofPreview] = useState<string | null>(
    null
  );

  // Fetch center data if in edit mode
  const { data: centerData } = useGetCenterById(id || "");
  const center = centerData?.data?.center;

  useEffect(() => {
    if (id && center) {
      setIsEdit(true);
      const updatedFormData = {
        name: center.name || "",
        code: center.code || "",
        type: center.type || "",
        zone: center.zone || "",
        phone: center.phone ? center.phone.toString() : "",
        email: center.email || "",
        address: center.address || "",
        district: center.district || "",
      };
      setFormData(updatedFormData);
      reset(updatedFormData);
    } else {
      setIsEdit(false);
      setFormData({
        name: "",
        code: "",
        type: "",
        zone: "",
        phone: "",
        email: "",
        address: "",
        district: "",
      });
      reset({});
    }
  }, [id, center, reset]);

  // Generic handler for image changes
  const handleImageChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    setFile: (file: File | null) => void,
    setPreview: (preview: string | null) => void
  ) => {
    if (event.target.files && event.target.files.length > 0) {
      const file = event.target.files[0];
      setFile(file);
      const previewUrl = URL.createObjectURL(file);
      setPreview(previewUrl);
    }
  };

  // Cleanup previews on unmount
  useEffect(() => {
    return () => {
      if (photoPreview) URL.revokeObjectURL(photoPreview);
      if (idCardPreview) URL.revokeObjectURL(idCardPreview);
      if (addressProofPreview) URL.revokeObjectURL(addressProofPreview);
    };
  }, [photoPreview, idCardPreview, addressProofPreview]);

  const handleSubmitForm = async (data: any) => {
    const payload = {
      ...data,
      phone: Number(data.phone),
    };

    let result;
    if (isEdit && id) {
      result = await updateCenter(id, payload);
    } else {
      result = await addNewCenter(payload);
    }

    if (result?.success) {
      showToast({
        title: "Success",
        text: `Center ${isEdit ? "updated" : "added"} successfully.`,
        icon: "success",
        timer: 5000,
      });
      navigate(all_routes.centerList);
    }
  };

  return (
    <div className="page-wrapper">
      <div className="content content-two">
        {/* Page Header */}
        <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
          <div className="my-auto mb-2">
            <h3 className="mb-1">{isEdit ? "Edit" : "Add"} Center</h3>
            <nav>
              <ol className="breadcrumb mb-0">
                <li className="breadcrumb-item">
                  <Link to={all_routes.adminDashboard}>Dashboard</Link>
                </li>
                <li className="breadcrumb-item">
                  <Link to={all_routes.centerList}>Centers</Link>
                </li>
                <li className="breadcrumb-item active">
                  {isEdit ? "Edit" : "Add"} Center
                </li>
              </ol>
            </nav>
          </div>
        </div>

        <div className="row">
          <div className="col-md-12">
            <form onSubmit={handleSubmit(handleSubmitForm)}>
              {/* Center Information Card */}
              <div className="card">
                <div className="card-header bg-light">
                  <div className="d-flex align-items-center">
                    <span className="bg-white avatar avatar-sm me-2 text-gray-7 flex-shrink-0">
                      <i className="ti ti-building-community fs-16" />
                    </span>
                    <h4 className="text-dark">Center Information</h4>
                  </div>
                </div>
                <div className="card-body pb-1">
                  <div className="row">
                    <div className="col-lg-6">
                      <div className="mb-3">
                        <label className="form-label">Center Name <span className="text-danger">*</span></label>
                        <Controller
                          name="name"
                          control={control}
                          render={({ field }) => (
                            <input
                              type="text"
                              className={`form-control ${errors.name ? 'is-invalid' : ''}`}
                              placeholder="Enter center name"
                              {...field}
                            />
                          )}
                        />
                        {errors.name && <div className="invalid-feedback">{errors.name.message}</div>}
                      </div>
                      <div className="mb-3">
                        <label className="form-label">Center Type <span className="text-danger">*</span></label>
                        <Controller
                          name="type"
                          control={control}
                          render={({ field }) => (
                            <CommonSelect
                              className="select"
                              options={centerTypes}
                              value={centerTypes.find(option => option.value === field.value) || undefined}
                              onChange={(value) => {
                                field.onChange(value?.value || "");
                              }}
                            />
                          )}
                        />
                        {errors.type && <div className="invalid-feedback">{errors.type.message}</div>}
                      </div>
                      <div className="mb-3">
                        <label className="form-label">Email <span className="text-danger">*</span></label>
                        <Controller
                          name="email"
                          control={control}
                          render={({ field }) => (
                            <input
                              type="email"
                              className={`form-control ${errors.email ? 'is-invalid' : ''}`}
                              placeholder="Enter email address"
                              {...field}
                            />
                          )}
                        />
                        {errors.email && <div className="invalid-feedback">{errors.email.message}</div>}
                      </div>
                    </div>
                    <div className="col-lg-6">
                      <div className="mb-3">
                        <label className="form-label">Center Code <span className="text-danger">*</span></label>
                        <Controller
                          name="code"
                          control={control}
                          render={({ field }) => (
                            <input
                              type="text"
                              className={`form-control ${errors.code ? 'is-invalid' : ''}`}
                              placeholder="Enter center code"
                              {...field}
                            />
                          )}
                        />
                        {errors.code && <div className="invalid-feedback">{errors.code.message}</div>}
                      </div>
                      <div className="mb-3">
                        <label className="form-label">Contact Number <span className="text-danger">*</span></label>
                        <Controller
                          name="phone"
                          control={control}
                          render={({ field }) => (
                            <input
                              type="tel"
                              className={`form-control ${errors.phone ? 'is-invalid' : ''}`}
                              placeholder="Enter contact number"
                              {...field}
                            />
                          )}
                        />
                        {errors.phone && <div className="invalid-feedback">{errors.phone.message}</div>}
                      </div>
                      <div className="mb-3">
                        <label className="form-label">Zone <span className="text-danger">*</span></label>
                        <Controller
                          name="zone"
                          control={control}
                          render={({ field }) => (
                            <CommonSelect
                              className="select"
                              options={zones}
                              value={zones.find(option => option.value === field.value) || undefined}
                              onChange={(value) => {
                                field.onChange(value?.value || "");
                              }}
                            />
                          )}
                        />
                        {errors.zone && <div className="invalid-feedback">{errors.zone.message}</div>}
                      </div>
                    </div>
                    <div className="col-lg-12">
                      <div className="mb-3">
                        <label className="form-label">District <span className="text-danger">*</span></label>
                        <Controller
                          name="district"
                          control={control}
                          render={({ field }) => (
                            <CommonSelect
                              className="select"
                              options={districts}
                              value={districts.find(option => option.value === field.value) || undefined}
                              onChange={(value) => {
                                field.onChange(value?.value || "");
                              }}
                            />
                          )}
                        />
                        {errors.district && <div className="invalid-feedback">{errors.district.message}</div>}
                      </div>
                      <div className="mb-3">
                        <label className="form-label">Center Address <span className="text-danger">*</span></label>
                        <Controller
                          name="address"
                          control={control}
                          render={({ field }) => (
                            <textarea
                              rows={3}
                              className={`form-control ${errors.address ? 'is-invalid' : ''}`}
                              placeholder="Enter center address"
                              {...field}
                            />
                          )}
                        />
                        {errors.address && <div className="invalid-feedback">{errors.address.message}</div>}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Center Documents Section */}
              {isEdit && (
                <div className="card mb-3">
                  <div className="card-header bg-light">
                    <div className="d-flex align-items-center">
                      <span className="bg-white avatar avatar-sm me-2 text-gray-7 flex-shrink-0">
                        <i className="ti ti-file-description fs-16" />
                      </span>
                      <h4 className="text-dark">Additional Documents</h4>
                    </div>
                  </div>
                  <div className="card-body">
                    {/* Center Photo */}
                    <div className="row mb-4">
                      <div className="col-md-12">
                        <label className="form-label">Center Photo</label>
                        <div className="d-flex align-items-center flex-wrap row-gap-3">
                          <div className="d-flex align-items-center justify-content-center avatar avatar-xxl border border-dashed me-2 flex-shrink-0 text-dark frames">
                            {photoPreview ? (
                              <img
                                src={photoPreview}
                                alt="Center Photo"
                                style={{
                                  width: "100%",
                                  height: "100%",
                                  objectFit: "cover",
                                  borderRadius: "50%",
                                }}
                              />
                            ) : (
                              <i className="ti ti-photo-plus fs-16" />
                            )}
                          </div>
                          <div className="profile-upload">
                            <div className="profile-uploader d-flex align-items-center">
                              <div className="drag-upload-btn mb-3">
                                Upload
                                <input
                                  type="file"
                                  className="form-control image-sign"
                                  onChange={(e) =>
                                    handleImageChange(
                                      e,
                                      setPhotoFile,
                                      setPhotoPreview
                                    )
                                  }
                                  accept="image/*"
                                />
                              </div>
                              {photoPreview && (
                                <button
                                  type="button"
                                  className="btn btn-primary mb-3 ms-2"
                                  onClick={() => {
                                    setPhotoFile(null);
                                    setPhotoPreview(null);
                                  }}
                                >
                                  Remove
                                </button>
                              )}
                            </div>
                            <p className="fs-12">
                              Upload image size 1MB, Format JPG, PNG
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Identity Card */}
                    <div className="row mb-4">
                      <div className="col-md-12">
                        <label className="form-label">Identity Card</label>
                        <div className="d-flex align-items-center flex-wrap row-gap-3">
                          <div className="d-flex align-items-center justify-content-center avatar avatar-xxl border border-dashed me-2 flex-shrink-0 text-dark frames">
                            {idCardPreview ? (
                              <img
                                src={idCardPreview}
                                alt="Identity Card"
                                style={{
                                  width: "100%",
                                  height: "100%",
                                  objectFit: "cover",
                                  borderRadius: "50%",
                                }}
                              />
                            ) : (
                              <i className="ti ti-id fs-16" />
                            )}
                          </div>
                          <div className="profile-upload">
                            <div className="profile-uploader d-flex align-items-center">
                              <div className="drag-upload-btn mb-3">
                                Upload
                                <input
                                  type="file"
                                  className="form-control image-sign"
                                  onChange={(e) =>
                                    handleImageChange(
                                      e,
                                      setIdCardFile,
                                      setIdCardPreview
                                    )
                                  }
                                  accept="image/*"
                                />
                              </div>
                              {idCardPreview && (
                                <button
                                  type="button"
                                  className="btn btn-primary mb-3 ms-2"
                                  onClick={() => {
                                    setIdCardFile(null);
                                    setIdCardPreview(null);
                                  }}
                                >
                                  Remove
                                </button>
                              )}
                            </div>
                            <p className="fs-12">
                              Upload image size 1MB, Format JPG, PNG
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Address Proof */}
                    <div className="row">
                      <div className="col-md-12">
                        <label className="form-label">Address Proof</label>
                        <div className="d-flex align-items-center flex-wrap row-gap-3">
                          <div className="d-flex align-items-center justify-content-center avatar avatar-xxl border border-dashed me-2 flex-shrink-0 text-dark frames">
                            {addressProofPreview ? (
                              <img
                                src={addressProofPreview}
                                alt="Address Proof"
                                style={{
                                  width: "100%",
                                  height: "100%",
                                  objectFit: "cover",
                                  borderRadius: "50%",
                                }}
                              />
                            ) : (
                              <i className="ti ti-file-upload fs-16" />
                            )}
                          </div>
                          <div className="profile-upload">
                            <div className="profile-uploader d-flex align-items-center">
                              <div className="drag-upload-btn mb-3">
                                Upload
                                <input
                                  type="file"
                                  className="form-control image-sign"
                                  onChange={(e) =>
                                    handleImageChange(
                                      e,
                                      setAddressProofFile,
                                      setAddressProofPreview
                                    )
                                  }
                                  accept="image/*"
                                />
                              </div>
                              {addressProofPreview && (
                                <button
                                  type="button"
                                  className="btn btn-primary mb-3 ms-2"
                                  onClick={() => {
                                    setAddressProofFile(null);
                                    setAddressProofPreview(null);
                                  }}
                                >
                                  Remove
                                </button>
                              )}
                            </div>
                            <p className="fs-12">
                              Upload image size 1MB, Format JPG, PNG
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Form buttons */}
              <div className="text-end">
                <Link to={all_routes.centerList} className="btn btn-light me-3">
                  Cancel
                </Link>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={isAdding || isUpdating}
                >
                  {isAdding
                    ? "Saving..."
                    : isUpdating
                    ? "Updating..."
                    : isEdit
                    ? "Update Center"
                    : "Add Center"}
                </button>
              </div>
            </form>
            {addError && (
              <div className="alert alert-danger mt-3">{addError}</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddCenter;
